// ADR-133: Reports → Ingestion Sync Runs — run detail (page 2 of list/detail).
//
// Mirrors the Direct Representative Discrepancy Audit run detail: summary
// tiles, filters, an infinitely-loading table, a stored-record dialog. Items
// are browsed one result section at a time (hired / updated / skipped / …);
// sections hidden from the UI (e.g. `unchanged`) are listed but disabled.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import {
  Accordion, AccordionDetails, AccordionSummary, CircularProgress, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useOpenIndividual from '../../../hooks/useOpenIndividual';
import { INDIVIDUAL_ENTRY_POINTS } from '../../../constants/individualOpenModes';
import {
  Container, Header, HeaderLeft, Title, Content, MainSection, BackButton,
} from '../styles';
import { useSyncRunDetail, useSyncRunItems } from '../../../hooks/useIngestionSyncRuns';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { formatSectionLabel } from '../../../utils/ingestionSyncRuns';
import { INGESTION_SYNC_RUNS_PATH } from '../../../constants/ingestionSyncRuns';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import JsonViewer from '../../common/JsonViewer';
import SummaryCard from './SummaryCard';
import BatchSteps from './BatchSteps';
import ItemsFilterBar from './ItemsFilterBar';
import ItemsTable from './ItemsTable';
import ItemRecordDialog from './ItemRecordDialog';
import { LoadMoreButton } from './styles';

const PAGE_LIMIT = 50;

const pickDefaultSection = (sections = []) => sections.find((s) => !s.hidden && s.itemCount > 0)?.sectionKey
  || sections.find((s) => !s.hidden)?.sectionKey
  || '';

const RunDetailPage = () => {
  const navigate = useNavigate();
  const openIndividual = useOpenIndividual();
  const location = useLocation();
  const { runId } = useParams();
  const ability = useAbility();
  const canReadRecord = ability.can(ACTIONS.READ, SUBJECTS.INGESTION_SYNC_RUN_RECORD);
  const [recordItem, setRecordItem] = useState(null);

  const restored = location.state?.runDetailState?.runId === runId ? location.state.runDetailState : null;

  // Opened from another page (e.g. Job Position Mappings → Unmapped → "Open run"):
  // Back returns there. Kept across the individual page and sibling batch steps.
  // Back from the individual page, location.state.returnTo is THIS page, so the
  // restored bag wins; a self-link is never used.
  const originState = restored || location.state || {};
  const origin = originState.returnTo;
  const returnTo = typeof origin === 'string' && origin.startsWith('/') && !origin.startsWith('//')
    && origin.split('?')[0] !== location.pathname ? origin : null;
  const returnLabel = returnTo ? (originState.returnLabel || 'previous page') : null;

  const { detail, isLoading: runLoading, error: runError } = useSyncRunDetail(runId);
  const run = detail?.run || null;
  const sections = useMemo(() => detail?.sections || [], [detail]);

  const [filters, setFilters] = useState(() => ({
    section: '', q: '', employee: null, ...(restored?.filters || {}),
  }));

  // Navigating between runs of the same batch reuses this component.
  useEffect(() => {
    if (!restored) {
      setFilters({ section: '', q: '', employee: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Pick the largest visible section once the run's sections are known.
  useEffect(() => {
    if (!sections.length) {
      return;
    }
    const current = sections.find((s) => s.sectionKey === filters.section && !s.hidden);
    if (!current) {
      setFilters((prev) => ({ ...prev, section: pickDefaultSection(sections) }));
    }
  }, [sections, filters.section]);

  const apiFilters = useMemo(() => ({
    runId,
    section: filters.section || undefined,
    q: filters.q || undefined,
    employeeId: filters.employee?.id || undefined,
    limit: PAGE_LIMIT,
  }), [runId, filters]);

  const {
    items, pageInfo, isLoading: itemsLoading, isLoadingMore, error: itemsError, loadMore,
  } = useSyncRunItems(apiFilters);

  const mainSectionRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (!itemsLoading && restored?.scrollTop && !scrollRestoredRef.current && mainSectionRef.current) {
      mainSectionRef.current.scrollTop = restored.scrollTop;
      scrollRestoredRef.current = true;
    }
  }, [itemsLoading, restored]);

  const handleNavigateToEmployee = (employeeId) => {
    openIndividual(employeeId, {
      entryPoint: INDIVIDUAL_ENTRY_POINTS.INGESTION_SYNC_RUNS,
      returnTo: location.pathname + location.search,
      returnLabel: 'Sync Run',
      state: {
        runDetailState: {
          runId, filters, scrollTop: mainSectionRef.current?.scrollTop || 0, returnTo, returnLabel,
        },
      },
    });
  };

  const hasSummary = run?.summary && typeof run.summary === 'object' && Object.keys(run.summary).length > 0;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(returnTo || INGESTION_SYNC_RUNS_PATH)}>
            <FaArrowLeft /> {returnTo ? `Back to ${returnLabel}` : 'Back to Runs'}
          </BackButton>
          <Title>Sync Run Detail</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <MainSection ref={mainSectionRef}>
          {runLoading && <LoadingSpinner />}
          {!runLoading && runError && (
            <EmptyState title="Failed to load run" description={runError?.message || 'Run not found or unreachable.'} />
          )}
          {!runLoading && !runError && !run && (
            <EmptyState title="Run not found" description="This run does not exist or was removed after 90 days." />
          )}
          {!runLoading && !runError && run && (
            <>
              <SummaryCard run={run} batch={detail.batch} />
              <BatchSteps
                batch={detail.batch}
                currentRunId={run.id}
                onOpenRun={(id) => navigate(`${INGESTION_SYNC_RUNS_PATH}/${id}`, {
                  state: returnTo ? { returnTo, returnLabel } : undefined,
                })}
              />

              {hasSummary && (
                <Accordion disableGutters sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Run summary</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <JsonViewer value={run.summary} defaultExpandDepth={1} />
                  </AccordionDetails>
                </Accordion>
              )}

              {sections.length === 0 ? (
                <EmptyState
                  title="No items recorded"
                  description={run.resultStatus === 'PENDING'
                    ? 'The result is still uploading — refresh in a minute.'
                    : 'This run produced no item lists (only counters).'}
                />
              ) : (
                <>
                  <ItemsFilterBar
                    sections={sections}
                    section={filters.section}
                    employee={filters.employee}
                    q={filters.q}
                    onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
                  />

                  <ResultsCount
                    count={pageInfo?.totalCount || 0}
                    singular={`${formatSectionLabel(filters.section).toLowerCase()} item`}
                    plural={`${formatSectionLabel(filters.section).toLowerCase()} items`}
                  />

                  {itemsLoading && <LoadingSpinner />}
                  {!itemsLoading && itemsError && (
                    <EmptyState title="Failed to load items" description={itemsError?.message || 'Try refreshing.'} />
                  )}
                  {!itemsLoading && !itemsError && items.length === 0 && (
                    <EmptyState title="No items match these filters" description="Try another section or clear the search." />
                  )}
                  {!itemsLoading && !itemsError && items.length > 0 && (
                    <>
                      <ItemsTable
                        run={run}
                        sectionKey={filters.section}
                        items={items}
                        onNavigateToEmployee={handleNavigateToEmployee}
                        onViewRecord={canReadRecord ? setRecordItem : undefined}
                      />
                      {pageInfo?.hasNextPage && (
                        <LoadMoreButton onClick={loadMore} disabled={isLoadingMore}>
                          {isLoadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                          Load More
                        </LoadMoreButton>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </MainSection>
      </Content>

      <ItemRecordDialog item={recordItem} onClose={() => setRecordItem(null)} />
    </Container>
  );
};

export default RunDetailPage;
